import {Button, Card, DataStatus, openDialog, Segment, Tooltip} from 'platform/components';
import {VStack, Box, HStack, Show} from 'platform/foundation';

import {useState} from 'react';

import {useGetOrderProfitabilityQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {OrderTrackedTime} from './OrderTrackedTime';
import {ProfitabilityFinances} from './ProfitabilityFinances';
import {ProfitabilityWorkingTime} from './ProfitabilityWorkingTime';

type ProfitabilityType = 'finances' | 'time';

interface ProfitabilitySummaryProps extends TestIdProps {
  serviceCaseId: string;
  isReportButtonDisabled: boolean;
  canReadServiceOrderFinancesProfitability: boolean;
}

export function ProfitabilitySummary(props: ProfitabilitySummaryProps) {
  const {orderId, serviceCaseId} = useWorkshopUrl();
  const [type, setType] = useState<ProfitabilityType>('time');

  const [canReadChronos] = usePermissions({
    permissionKeys: ['chronosUser'],
  });

  const {data, isLoading, isError} = useGetOrderProfitabilityQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: orderId,
  });

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={46}>
      <Card variant="inlineGrey">
        <VStack spacing={4}>
          <HStack justify="space-between">
            <Box width={75}>
              <Segment
                value={type}
                onChange={setType}
                options={[
                  {
                    value: 'finances',
                    label: !props.canReadServiceOrderFinancesProfitability ? (
                      <Tooltip label={i18n.t('general.labels.noPermission')}>
                        {i18n.t('entity.order.labels.finances')}
                      </Tooltip>
                    ) : (
                      i18n.t('entity.order.labels.finances')
                    ),
                    isDisabled: !props.canReadServiceOrderFinancesProfitability,
                  },
                  {value: 'time', label: i18n.t('entity.order.labels.workingTime')},
                ]}
              />
            </Box>
            <Show when={type === 'time'}>
              <Button
                title={i18n.t('entity.order.labels.report')}
                variant="link"
                isDisabled={props.isReportButtonDisabled || !canReadChronos}
                leftIcon="devices/access_time"
                data-testid={suffixTestId('report', props)}
                onClick={() =>
                  openDialog(
                    <OrderTrackedTime serviceCaseId={serviceCaseId} serviceOrderId={orderId} />,
                    {title: i18n.t('entity.orderRequest.labels.workReport')}
                  )
                }
              />
            </Show>
          </HStack>
          <Show when={type === 'finances'}>
            <ProfitabilityFinances
              profitabilityData={data}
              data-testid={suffixTestId('finances', props)}
            />
          </Show>
          <Show when={type === 'time'}>
            <ProfitabilityWorkingTime
              canReadChronos={canReadChronos}
              profitabilityData={data}
              data-testid={suffixTestId('workingTime', props)}
            />
          </Show>
        </VStack>
      </Card>
    </DataStatus>
  );
}
